import React, { useState } from 'react'
import {
  Box,
  TextField,
  Grid,
  Divider,
  FormControlLabel,
  Switch,
  Typography,
  Button
} from '@mui/material'
import SendIcon from '@mui/icons-material/Send'

const TeamDistributionListsForm = ({ 
  initialData = {
    productTeamDL: '',
    developmentTeamDL: '',
    sreTeamDL: '',
    additionalDL: ''
  },
  onSubmit,
  onChange,
  submitButtonText = 'Submit',
  loading = false,
  readOnly = false
}) => {
  const [formData, setFormData] = useState(initialData)
  const [showAdditionalDL, setShowAdditionalDL] = useState(!!initialData.additionalDL)
  const [errors, setErrors] = useState({
    productTeamDL: '',
    developmentTeamDL: '',
    sreTeamDL: '',
    additionalDL: '',
  })

  const validateEmailList = (emailString) => {
    if (!emailString || emailString.trim() === '') return { valid: true, message: '' }
    
    const emails = emailString.split(',').map(email => email.trim())
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    
    for (let email of emails) {
      if (!emailRegex.test(email)) {
        return { valid: false, message: `Invalid email format: ${email}` }
      }
    }
    
    return { valid: true, message: '' }
  }

  const handleDLChange = (field, value) => {
    const updatedData = { ...formData, [field]: value }
    setFormData(updatedData)
    
    // Validate on change
    const validation = validateEmailList(value)
    setErrors({ ...errors, [field]: validation.message })
    
    // Call onChange callback if provided
    if (onChange) {
      onChange(updatedData)
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    
    // Validate all required fields
    if (!formData.productTeamDL || !formData.developmentTeamDL || !formData.sreTeamDL) {
      return
    }

    // Validate email formats
    const productTeamValidation = validateEmailList(formData.productTeamDL)
    if (!productTeamValidation.valid) return

    const devTeamValidation = validateEmailList(formData.developmentTeamDL)
    if (!devTeamValidation.valid) return

    const sreTeamValidation = validateEmailList(formData.sreTeamDL)
    if (!sreTeamValidation.valid) return

    if (formData.additionalDL) {
      const additionalValidation = validateEmailList(formData.additionalDL)
      if (!additionalValidation.valid) return
    }

    onSubmit(formData)
  }

  return (
    <Box component={readOnly ? "div" : "form"} onSubmit={readOnly ? undefined : handleSubmit}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {readOnly 
          ? "Team distribution lists for this product."
          : "Provide email distribution lists for all teams involved in certificate management. Use comma-separated email addresses for multiple contacts (e.g., team1@company.com, team2@company.com)."
        }
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Product Team DL *"
            value={formData.productTeamDL}
            onChange={(e) => handleDLChange('productTeamDL', e.target.value)}
            placeholder="product-team@broadridge.com, manager@broadridge.com"
            helperText={errors.productTeamDL || "Main product team contact (comma-separated for multiple emails)"}
            error={!!errors.productTeamDL}
            InputProps={{ readOnly }}
            required
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Development Team DL *"
            value={formData.developmentTeamDL}
            onChange={(e) => handleDLChange('developmentTeamDL', e.target.value)}
            placeholder="dev-team@broadridge.com, lead-dev@broadridge.com"
            helperText={errors.developmentTeamDL || "Development team for technical coordination (comma-separated for multiple emails)"}
            error={!!errors.developmentTeamDL}
            InputProps={{ readOnly }}
            required
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="SRE Team DL *"
            value={formData.sreTeamDL}
            onChange={(e) => handleDLChange('sreTeamDL', e.target.value)}
            placeholder="sre-team@broadridge.com, oncall@broadridge.com"
            helperText={errors.sreTeamDL || "Site Reliability Engineering team for operations (comma-separated for multiple emails)"}
            error={!!errors.sreTeamDL}
            InputProps={{ readOnly }}
            required
          />
        </Grid>

        {!readOnly && (
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <FormControlLabel
              control={
                <Switch
                  checked={showAdditionalDL}
                  onChange={(e) => {
                    setShowAdditionalDL(e.target.checked)
                    if (!e.target.checked) {
                      const updatedData = { ...formData, additionalDL: '' }
                      setFormData(updatedData)
                      setErrors({ ...errors, additionalDL: '' })
                      if (onChange) {
                        onChange(updatedData)
                      }
                    }
                  }}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2">
                  Add Additional Distribution List (Optional)
                </Typography>
              }
            />
          </Grid>
        )}

        {(showAdditionalDL || (readOnly && formData.additionalDL)) && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Additional DL"
              value={formData.additionalDL}
              onChange={(e) => handleDLChange('additionalDL', e.target.value)}
              placeholder="additional-team@broadridge.com, support@broadridge.com"
              helperText={errors.additionalDL || "Optional: Additional team distribution list (comma-separated for multiple emails)"}
              error={!!errors.additionalDL}
              InputProps={{ readOnly }}
            />
          </Grid>
        )}
      </Grid>

      {!readOnly && onSubmit && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={loading || !formData.productTeamDL || !formData.developmentTeamDL || !formData.sreTeamDL}
            endIcon={<SendIcon />}
          >
            {loading ? 'Submitting...' : submitButtonText}
          </Button>
        </Box>
      )}
    </Box>
  )
}

export default TeamDistributionListsForm
